import React, { useEffect, useState } from 'react';
import { Button, Pagination, Cascader, Select } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import styles from './index.module.scss';
import SearchConponent from '@/components/SearchConponent/SearchConponent';
import { getProductCategoryAllList } from '@/api/products';
import { getAllPublishedEmergencyServices } from '@/api/emergencyServiceApi';
import ServiceProductList from '@/components/ServiceProductList/ServiceProductList';

interface CascaderOption {
  value: number;
  label: string;
  children?: CascaderOption[];
  isLeaf?: boolean;
}

interface IClassification {
  is_active: boolean;
  is_delete: boolean;
  order: any;
  parent_id: number;
  product_category_id: number;
  product_category_name: string;
  child?: IClassification[];
}

const ServiceSearchList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [keyword, setKeyword] = useState<string>('');

  // 获取定位信息
  const latitude = useAppSelector((state) => state.user.latitude);
  const longitude = useAppSelector((state) => state.user.longitude);

  const [paramsService, setParamsService] = useState<any>({
    user_latitude: 40.71876530, // 默认坐标，如果没有用户坐标则使用这个
    user_longitude: -73.99002840,
    pageNum: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
    keyword: '',
    status: 0, // 只获取竞价中的服务
    sort_by: 'time_desc', // time_desc | time_asc | price_desc | price_asc
    time_range: 'any' // any | today | 3d | 7d
  });

  const [serviceGoodsList, setServiceGoodsList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [cascaderOptions, setCascaderOptions] = useState<CascaderOption[]>([]);
  const [cascaderValue, setCascaderValue] = useState<number[]>([]);

  // 下拉开合状态用于切换右侧小图标
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [distanceOpen, setDistanceOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);

  const renderSuffix = (open: boolean) => (
    <DownOutlined style={{ transition: 'transform .2s ease', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }} />
  );

  const transformToChildrenOptions = (children: IClassification[] = []): CascaderOption[] => {
    return children.map((child) => {
      const hasChildren = child.child && child.child.length > 0;
      return {
        value: child.product_category_id,
        label: child.product_category_name,
        children: hasChildren ? transformToChildrenOptions(child.child) : undefined,
        isLeaf: !hasChildren,
      };
    });
  };

  const transformToCascaderOptions = (data: IClassification[] = []): CascaderOption[] => {
    return data.map((item) => {
      const hasChildren = item.child && item.child.length > 0;
      return {
        value: item.product_category_id,
        label: item.product_category_name,
        children: hasChildren ? transformToChildrenOptions(item.child) : undefined,
        isLeaf: !hasChildren,
      };
    });
  };

  const displayRender = (labels: string[]): string => labels.join(' > ');

  const handleCascaderChange = (_value: any, selectedOptions: any): void => {
    if (!selectedOptions || selectedOptions.length === 0) return;
    const last = selectedOptions[selectedOptions.length - 1];
    if (!last.isLeaf) return;
    // 对于紧急服务，分类过滤可能需要通过其他方式实现
    // 暂时保持原有的参数结构，但可能需要调整API调用
    const myParams = { ...paramsService, product_category_id: last.value, pageNum: 1 };
    setParamsService(myParams);
    getServiceProductListFn(myParams);
  };

  const getServiceProductListFn = (query: any) => {
    setLoading(true);
    getAllPublishedEmergencyServices(query).then((res: any) => {
      if (res.code === 200) {
        const orderList = res.data?.data || res.data?.list || res.data || [];
        setServiceGoodsList(Array.isArray(orderList) ? orderList : []);
        setParamsService((prev: any) => ({
          ...prev,
          pageNum: query.pageNum,
          pageSize: query.pageSize,
          total: res.total || orderList.length || 0,
          totalPages: res.totalPages || 1,
        }));
      }
    }).catch((error) => {
      console.error('获取紧急服务列表失败:', error);
      setServiceGoodsList([]);
    }).finally(() => {
      setLoading(false);
    });
  };

  const getMessage = (inputValue: string): void => {
    const myParams = { ...paramsService, keyword: inputValue, pageNum: 1 };
    setKeyword(inputValue);
    setParamsService(myParams);
    getServiceProductListFn(myParams);
  };

  const [goToPage, setGoToPage] = useState<string>('');
  const paginationChange = (page: number, pageSize: number) => {
    const myParams = { ...paramsService, pageNum: page, pageSize };
    setParamsService(myParams);
    getServiceProductListFn(myParams);
  };
  const handleGoToPage = () => {
    const page = parseInt(goToPage);
    if (isNaN(page) || page < 1 || page > (paramsService.totalPages || 1)) return;
    paginationChange(page, paramsService.pageSize);
  };

  // 过滤项变更：分类、距离、价格排序、时间范围
  const handleDistanceChange = (value: number) => {
    const myParams = { ...paramsService, pageNum: 1, pageSize: value * 2 }; // 增加页面大小以显示更多结果
    setParamsService(myParams);
    getServiceProductListFn(myParams);
  };

  const handlePriceSortChange = (value: string) => {
    const myParams = { ...paramsService, sort_by: value, pageNum: 1 };
    setParamsService(myParams);
    getServiceProductListFn(myParams);
  };

  const handleTimeChange = (value: string) => {
    const myParams = { ...paramsService, time_range: value, pageNum: 1 };
    setParamsService(myParams);
    getServiceProductListFn(myParams);
  };

  const handleExpandRange = () => {
    const next = paramsService.pageSize < 50 ? 50 : 100; // 增加页面大小而不是距离
    const myParams = { ...paramsService, pageNum: 1, pageSize: next };
    setParamsService(myParams);
    getServiceProductListFn(myParams);
  };

  useEffect(() => {
    const kw = searchParams.get('keyword') || '';
    setKeyword(kw);
    
    // 使用用户坐标或默认坐标
    const userLat = latitude || 40.71876530;
    const userLng = longitude || -73.99002840;
    
    const myParams = { 
      ...paramsService, 
      keyword: kw,
      user_latitude: userLat,
      user_longitude: userLng,
    };
    setParamsService(myParams);
    getServiceProductListFn(myParams);

    getProductCategoryAllList(2).then((res: any) => {
      if (res.code === 200) {
        setCascaderOptions(transformToCascaderOptions(res.data || []));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 当用户坐标变化时重新获取数据
  useEffect(() => {
    if (latitude && longitude) {
      const myParams = { 
        ...paramsService, 
        user_latitude: latitude,
        user_longitude: longitude,
      };
      setParamsService(myParams);
      getServiceProductListFn(myParams);
    }
  }, [latitude, longitude]);

  return (
    <div className={styles['search-list']}>
      <SearchConponent keyword={keyword} getMessage={getMessage} />

      <div className={styles['tabs-list']} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className={`${styles['tabs-item']} ${styles['active']}`} style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <span>All Published Orders</span>
          <span style={{ color: '#8aa0a6', fontSize: 12 }}>(Show {paramsService.pageSize} Service Orders Per Page)</span>
        </div>
        <a onClick={handleExpandRange} style={{ color: '#1fb6c9', fontSize: 12, cursor: 'pointer' }}>Click To Load More Orders</a>
      </div>

      <div className={styles['search-Classification']} style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div className={styles['Classification-text']}>
          <Cascader
            value={cascaderValue}
            options={cascaderOptions}
            style={{ width: 220 }}
            expandTrigger="hover"
            displayRender={displayRender}
            open={categoryOpen}
            onDropdownVisibleChange={(open) => setCategoryOpen(open)}
            suffixIcon={renderSuffix(categoryOpen)}
            onChange={(value, selectedOptions) => {
              setCascaderValue(value || []);
              handleCascaderChange(value, selectedOptions);
            }}
            placeholder="Service Classification"
            className={styles['cascader-selector']}
            changeOnSelect={false}
            fieldNames={{ label: 'label', value: 'value', children: 'children' }}
            showSearch={{
              filter: (inputValue, path) => path.some((option) => option.label.toLowerCase().includes(inputValue.toLowerCase())),
              matchInputWidth: true,
              limit: 10,
            }}
          />
        </div>

        <Select
          value={paramsService.pageSize}
          style={{ width: 160 }}
          onChange={(value) => {
            const myParams = { ...paramsService, pageNum: 1, pageSize: value };
            setParamsService(myParams);
            getServiceProductListFn(myParams);
          }}
          open={distanceOpen}
          onDropdownVisibleChange={(open) => setDistanceOpen(open)}
          suffixIcon={renderSuffix(distanceOpen)}
          options={[20, 50, 100].map((size) => ({ label: `Show ${size} Orders`, value: size }))}
        />

        <Select
          value={paramsService.sort_by}
          style={{ width: 190 }}
          onChange={handlePriceSortChange}
          open={sortOpen}
          onDropdownVisibleChange={(open) => setSortOpen(open)}
          suffixIcon={renderSuffix(sortOpen)}
          options={[
            { label: 'Price From High To Low', value: 'price_desc' },
            { label: 'Price From Low To High', value: 'price_asc' },
          ]}
        />

        <Select
          value={paramsService.time_range}
          style={{ width: 140 }}
          onChange={handleTimeChange}
          open={timeOpen}
          onDropdownVisibleChange={(open) => setTimeOpen(open)}
          suffixIcon={renderSuffix(timeOpen)}
          options={[
            { label: 'Time', value: 'any' },
            { label: 'Today', value: 'today' },
            { label: 'Last 3 Days', value: '3d' },
            { label: 'Last 7 Days', value: '7d' },
          ]}
        />
      </div>

      <ServiceProductList goodsList={serviceGoodsList} />

      {serviceGoodsList.length > 0 && (
        <div className={styles['pagination-container']}>
          <div className={styles['pagination-info']}>Total {paramsService.total} Products</div>
          <Pagination
            onChange={paginationChange}
            current={paramsService.pageNum}
            pageSize={paramsService.pageSize}
            total={paramsService.total}
            showSizeChanger={false}
            responsive
            hideOnSinglePage={(paramsService.totalPages || 1) <= 1}
          />
          <div className={styles['go-to-page']}>
            go to
            <input
              className={styles['page-input']}
              type="text"
              value={goToPage}
              onChange={(e) => setGoToPage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleGoToPage();
              }}
            />
            page
            <Button size="small" style={{ marginLeft: 8 }} onClick={handleGoToPage}>
              GO
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceSearchList;


